import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { Store } from '@ngrx/store';

import type { User } from '../../../../../../store/user/user.models';
import { selectAllUsers, selectUser } from '../../../../../../store/user/user.selectors';
import { MatDivider } from "@angular/material/divider";
import { BRANDING } from '../../../../../../core/config/branding';

export type CalendarEventDialogData = {
  mode: 'create' | 'edit';
  event?: {
    id: string;
    event_type?: 'in_person' | 'online';
    title: string;
    description?: string;
    location?: string;
    start_at: string;
    end_at: string;
    all_day: boolean;
    attendee_user_ids?: string[];
    attendee_emails?: string[];
    attendee_external?: Array<{ name: string; email: string }>;
    recurrence_frequency?: string;
    recurrence_interval?: number;
    recurrence_until?: string | null;
    meeting_url?: string;
  };
  start_at: string;
  end_at: string;
  all_day: boolean;
};

export type CalendarEventDialogResult =
  | {
      type: 'create';
      payload: {
        event_type: 'in_person' | 'online';
        title: string;
        description?: string;
        location?: string;
        start_at: string;
        end_at: string;
        all_day: boolean;
        attendee_user_ids?: string[];
        attendee_emails?: string[];
        attendee_external?: Array<{ name: string; email: string }>;
        recurrence_frequency?: string;
        recurrence_interval?: number;
        recurrence_until?: string;
      };
      attachMeetrixLink?: boolean;
    }
  | {
      type: 'update';
      eventId: string;
      payload: {
        event_type?: 'in_person' | 'online';
        title?: string;
        description?: string;
        location?: string;
        start_at?: string;
        end_at?: string;
        all_day?: boolean;
        attendee_user_ids?: string[];
        attendee_emails?: string[];
        attendee_external?: Array<{ name: string; email: string }>;
        recurrence_frequency?: string;
        recurrence_interval?: number;
        recurrence_until?: string;
      };
      attachMeetrixLink?: boolean;
    }
  | { type: 'delete'; eventId: string }
  | { type: 'cancel' };

@Component({
  selector: 'app-calendar-event-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule, MatSelectModule, MatOptionModule, MatDivider, MatChipsModule],
  templateUrl: './calendar-event-dialog.component.html',
  styleUrl: './calendar-event-dialog.component.scss',
})
export class CalendarEventDialogComponent {
  protected readonly brand = BRANDING;
  private readonly dialogRef = inject(MatDialogRef<CalendarEventDialogComponent, CalendarEventDialogResult>);
  protected readonly data = inject<CalendarEventDialogData>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly companyUsers = signal<User[]>([]);
  protected readonly currentUserId = signal<string | null>(null);
  protected readonly selectableCompanyUsers = signal<User[]>([]);
  protected readonly externalAttendees = signal<Array<{ name: string; email: string }>>(this.normalizeExternalAttendees(this.data.event));

  protected readonly form = this.formBuilder.group({
    eventType: [this.inferEventType(), [Validators.required]],
    title: [this.data.event?.title ?? '', [Validators.required, Validators.minLength(2)]],
    startAt: [this.toLocalInputValue(this.data.event?.start_at ?? this.data.start_at), [Validators.required]],
    endAt: [this.toLocalInputValue(this.data.event?.end_at ?? this.data.end_at), [Validators.required]],
    location: [this.data.event?.location ?? ''],
    description: [this.data.event?.description ?? ''],
    attendeeUserIds: [this.data.event?.attendee_user_ids ?? []],
    externalName: [''],
    externalEmail: ['', [Validators.email]],
    recurrenceFrequency: [this.normalizeRecurrenceFrequency(this.data.event?.recurrence_frequency)],
    recurrenceInterval: [Math.max(1, Number(this.data.event?.recurrence_interval ?? 1))],
    recurrenceUntil: [this.toLocalDateInputValue(this.data.event?.recurrence_until ?? null)],
  });

  constructor() {
    this.store
      .select(selectAllUsers)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((users) => {
        const list = users ?? [];
        this.companyUsers.set(list);
        this.updateSelectableCompanyUsers(list, this.currentUserId());
      });

    this.store
      .select(selectUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        const id = user?.id ?? null;
        this.currentUserId.set(id);
        this.updateSelectableCompanyUsers(this.companyUsers(), id);
      });

    this.syncLocationDisabledState(this.form.controls.eventType.value);
    this.syncRecurrenceDisabledState(this.form.controls.recurrenceFrequency.value);

    this.form.controls.eventType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.syncLocationDisabledState(value));

    this.form.controls.recurrenceFrequency.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.syncRecurrenceDisabledState(value));
  }

  protected close(): void {
    this.dialogRef.close({ type: 'cancel' });
  }

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.flushExternalAttendeeDraft()) {
      return;
    }

    const recurrenceFrequencyValue = this.form.controls.recurrenceFrequency.value ?? 'none';
    const recurrence_frequency = recurrenceFrequencyValue === 'none' ? '' : recurrenceFrequencyValue;
    const rawInterval = Number(this.form.controls.recurrenceInterval.value ?? 1);
    const recurrence_interval = recurrence_frequency ? Math.max(1, rawInterval || 1) : 0;
    const recurrence_until =
      recurrence_frequency && (this.form.controls.recurrenceUntil.value ?? '').trim() !== ''
        ? this.fromLocalDateInputValue(this.form.controls.recurrenceUntil.value ?? '')
        : '';

    const event_type = (this.form.controls.eventType.value ?? 'in_person') === 'online' ? 'online' : 'in_person';
    const payload = {
      event_type,
      title: this.form.controls.title.value?.trim() ?? '',
      description: this.form.controls.description.value?.trim() ?? '',
      location: (this.form.controls.eventType.value ?? 'in_person') === 'online' ? '' : (this.form.controls.location.value?.trim() ?? ''),
      start_at: this.fromLocalInputValue(this.form.controls.startAt.value ?? ''),
      end_at: this.fromLocalInputValue(this.form.controls.endAt.value ?? ''),
      all_day: !!this.data.all_day,
      attendee_user_ids: (this.form.controls.attendeeUserIds.value ?? []).filter(Boolean),
      attendee_external: this.externalAttendees(),
      attendee_emails: this.externalAttendees().map((attendee) => attendee.email),
      recurrence_frequency,
      recurrence_interval,
      recurrence_until,
    };

    if (this.data.mode === 'edit' && this.data.event?.id) {
      const attachMeetrixLink =
        (this.form.controls.eventType.value ?? 'in_person') === 'online' && !this.data.event?.meeting_url;
      this.dialogRef.close({ type: 'update', eventId: this.data.event.id, payload, attachMeetrixLink });
      return;
    }

    const attachMeetrixLink = (this.form.controls.eventType.value ?? 'in_person') === 'online';
    this.dialogRef.close({ type: 'create', payload, attachMeetrixLink });
  }

  protected delete(): void {
    if (!this.data.event?.id) {
      return;
    }
    this.dialogRef.close({ type: 'delete', eventId: this.data.event.id });
  }

  protected addExternalAttendee(): void {
    this.addExternalAttendeeFromDraft();
  }

  protected removeExternalAttendee(email: string): void {
    const normalized = email.trim().toLowerCase();
    this.externalAttendees.set(this.externalAttendees().filter((value) => value.email.toLowerCase() !== normalized));
  }

  private flushExternalAttendeeDraft(): boolean {
    return this.upsertExternalAttendeeFromDraft({ allowEmpty: true });
  }

  private addExternalAttendeeFromDraft(): void {
    this.upsertExternalAttendeeFromDraft({ allowEmpty: false });
  }

  private upsertExternalAttendeeFromDraft(options: { allowEmpty: boolean }): boolean {
    const name = this.form.controls.externalName.value?.trim() ?? '';
    const email = this.form.controls.externalEmail.value?.trim() ?? '';
    const nameControl = this.form.controls.externalName;
    const emailControl = this.form.controls.externalEmail;

    if (!name && !email && options.allowEmpty) {
      return true;
    }

    if (!name) {
      nameControl.setErrors({ required: true });
      nameControl.markAsTouched();
      return false;
    }
    if (nameControl.hasError('required')) {
      nameControl.setErrors(null);
    }

    if (!email || emailControl.invalid) {
      if (!email) {
        emailControl.setErrors({ required: true });
      } else {
        emailControl.setErrors(emailControl.errors ?? { email: true });
      }
      emailControl.markAsTouched();
      return false;
    }
    if (emailControl.hasError('required')) {
      emailControl.setErrors(null);
    }

    const normalized = this.normalizeExternalAttendeesFromDraft(name, email);
    if (!normalized) {
      emailControl.setErrors({ email: true });
      emailControl.markAsTouched();
      return false;
    }

    const next = this.normalizeExternalAttendeesList([...this.externalAttendees(), normalized]);
    this.externalAttendees.set(next);
    this.form.controls.externalName.setValue('');
    this.form.controls.externalEmail.setValue('');
    return true;
  }

  private toLocalInputValue(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private fromLocalInputValue(localValue: string): string {
    const date = new Date(localValue);
    return date.toISOString();
  }

  private normalizeRecurrenceFrequency(value: string | undefined): 'none' | 'daily' | 'weekly' | 'monthly' {
    if (value === 'daily' || value === 'weekly' || value === 'monthly') {
      return value;
    }
    return 'none';
  }

  private toLocalDateInputValue(isoOrNull: string | null | undefined): string {
    if (!isoOrNull) {
      return '';
    }

    const date = new Date(isoOrNull);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private fromLocalDateInputValue(localValue: string): string {
    if (!localValue) {
      return '';
    }

    const date = new Date(`${localValue}T23:59:59.999`);
    return date.toISOString();
  }

  private inferEventType(): 'in_person' | 'online' {
    if (this.data.mode === 'edit') {
      if (this.data.event?.event_type === 'online' || this.data.event?.event_type === 'in_person') {
        return this.data.event.event_type;
      }
      return this.data.event?.meeting_url ? 'online' : 'in_person';
    }
    return 'in_person';
  }

  private syncLocationDisabledState(value: unknown): void {
    const eventType = value === 'online' ? 'online' : 'in_person';
    const locationControl = this.form.controls.location;

    if (eventType === 'online') {
      locationControl.clearValidators();
      if (locationControl.enabled) {
        locationControl.disable({ emitEvent: false });
      }
      if ((locationControl.value ?? '').trim() !== '') {
        locationControl.setValue('', { emitEvent: false });
      }
      locationControl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (locationControl.disabled) {
      locationControl.enable({ emitEvent: false });
    }
    locationControl.setValidators([Validators.required]);
    locationControl.updateValueAndValidity({ emitEvent: false });
  }

  private syncRecurrenceDisabledState(value: unknown): void {
    const frequency = value === 'daily' || value === 'weekly' || value === 'monthly' ? value : 'none';
    const shouldDisable = frequency === 'none';
    const intervalControl = this.form.controls.recurrenceInterval;
    const untilControl = this.form.controls.recurrenceUntil;

    if (shouldDisable) {
      intervalControl.clearValidators();
      untilControl.clearValidators();
      if (intervalControl.enabled) {
        intervalControl.disable({ emitEvent: false });
      }
      if (untilControl.enabled) {
        untilControl.disable({ emitEvent: false });
      }
      untilControl.setValue('', { emitEvent: false });
      intervalControl.updateValueAndValidity({ emitEvent: false });
      untilControl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (intervalControl.disabled) {
      intervalControl.enable({ emitEvent: false });
    }
    if (untilControl.disabled) {
      untilControl.enable({ emitEvent: false });
    }
    intervalControl.setValidators([Validators.required, Validators.min(1)]);
    untilControl.setValidators([Validators.required]);
    intervalControl.updateValueAndValidity({ emitEvent: false });
    untilControl.updateValueAndValidity({ emitEvent: false });
  }

  private normalizeExternalAttendees(event: CalendarEventDialogData['event'] | undefined): Array<{ name: string; email: string }> {
    const list = event?.attendee_external?.length
      ? event.attendee_external
      : (event?.attendee_emails ?? []).map((email) => ({ name: '', email }));
    return this.normalizeExternalAttendeesList(list);
  }

  private updateSelectableCompanyUsers(users: User[], currentUserId: string | null): void {
    if (!currentUserId) {
      this.selectableCompanyUsers.set(users);
      return;
    }

    this.selectableCompanyUsers.set(users.filter((user) => user?.id !== currentUserId));
  }

  private normalizeExternalAttendeesFromDraft(name: string, email: string): { name: string; email: string } | null {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !/.+@.+\..+/.test(normalizedEmail)) {
      return null;
    }
    return { name: name.trim(), email: normalizedEmail };
  }

  private normalizeExternalAttendeesList(values: Array<{ name: string; email: string }>): Array<{ name: string; email: string }> {
    const seen = new Set<string>();
    return values
      .map((value) => ({ name: (value?.name ?? '').trim(), email: (value?.email ?? '').trim().toLowerCase() }))
      .filter((value) => value.email && /.+@.+\..+/.test(value.email))
      .filter((value) => {
        if (seen.has(value.email)) {
          return false;
        }
        seen.add(value.email);
        return true;
      });
  }
}
